/**
 * The colour of a forecast table cell.
 *
 * Straight from Edd's XC Weather screenshots: the table is readable at a glance
 * because the number sits on a colour, so a windy afternoon is a stripe of
 * orange you can see from across the cabin without reading a digit. The colours
 * are the SAME ramps the chart overlay uses (fieldRamps), deliberately: a 25 kn
 * cell in the table and a 25 kn wash on the chart must be the same colour, or
 * the two views teach two different colour languages for one quantity.
 *
 * Wind and gusts used to stay banded on the boundaries; the field test said
 * otherwise (fb-msmaxrq9-2: "instead of step changes in colour can we have a
 * gradient?"). A table is read cell against neighbouring cell, and a hard band
 * edge between 16 and 17 knots reads as a weather change that is not there,
 * where the chart's arrows, read singly, keep their categorical steps. So the
 * cells blend along the same ramp the wash uses, boundaries and all.
 */
import { windColourSteps } from '@/map/windArrow';
import { rampColour } from '@/map/fieldRaster';
import { fieldVariable, hexRgb } from '@/map/fieldRamps';

export interface CellPaint {
  bg: string;
  /** Dark ink on pale cells, white on saturated ones, so the number survives. */
  fg: string;
}

/** Relative luminance, 0..1, the standard sRGB weighting. */
export function luminance(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function paint(r: number, g: number, b: number): CellPaint {
  return {
    bg: `rgb(${r}, ${g}, ${b})`,
    fg: luminance(r, g, b) > 0.55 ? '#10182a' : '#ffffff',
  };
}

/**
 * The paint for one cell, or null for "leave it plain".
 *
 * Null is a real answer, not a failure: no value, a variable with no ramp
 * (direction, cloud), or rain under the drizzle threshold, where colouring a
 * dry hour blue would make every dry day look wet from across the cabin.
 */
export function cellPaint(variableId: string, value: number | null): CellPaint | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (variableId === 'wind' || variableId === 'gust') {
    const stops = windColourSteps().map((s) => ({ value: s.fromKn, rgb: hexRgb(s.colour) }));
    const [r, g, b] = rampColour(stops, value);
    return paint(r, g, b);
  }
  const v = fieldVariable(variableId);
  if (!v) return null;
  if (v.hideBelow != null && value < v.hideBelow) return null;
  const [r, g, b] = rampColour(v.stops, value);
  return paint(r, g, b);
}

/**
 * The arrow glyph's rotation for a wind FROM bearing, degrees.
 *
 * The arrow points the way the wind is GOING, matching the chart arrows and
 * every consumer weather app, while the numbers stay "from". The glyph itself
 * (↑) points up at rotation 0, so going-direction IS the rotation.
 */
export function arrowRotationDeg(dirFromDeg: number): number {
  return (dirFromDeg + 180) % 360;
}
